import { useEffect, useRef, useState } from 'react';
import { fetchProtectedImage } from '../utils/protectedImageLoader';

interface AuthenticatedImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onError'> {
  src?: string;
  enabled?: boolean;
  onLoadError?: (error: unknown) => void;
  requestPriority?: 'high' | 'normal';
}

const AuthenticatedImage = ({
  src,
  alt,
  enabled = true,
  onLoadError,
  requestPriority = 'normal',
  ...imageProps
}: AuthenticatedImageProps) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const onLoadErrorRef = useRef(onLoadError);

  useEffect(() => {
    onLoadErrorRef.current = onLoadError;
  }, [onLoadError]);

  useEffect(() => {
    setFailed(false);
    if (!src || !enabled) {
      setObjectUrl(null);
      return;
    }

    let active = true;
    let nextObjectUrl: string | null = null;
    const controller = new AbortController();

    fetchProtectedImage(src, controller.signal, requestPriority)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load protected image (${response.status})`);
        }
        return response.blob();
      })
      .then(blob => {
        if (!active) return;
        nextObjectUrl = URL.createObjectURL(blob);
        setObjectUrl(nextObjectUrl);
      })
      .catch(error => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        if (active) {
          console.warn('Protected image is unavailable:', error);
          setObjectUrl(null);
          setFailed(true);
          onLoadErrorRef.current?.(error);
        }
      });

    return () => {
      active = false;
      controller.abort();
      if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl);
    };
  }, [enabled, requestPriority, src]);

  if (!objectUrl) {
    return (
      <span
        role="img"
        aria-label={
          failed
            ? `${alt || 'Protected image'} unavailable`
            : enabled
              ? alt || 'Loading protected image'
              : `${alt || 'Protected image'} not loaded`
        }
        style={{
          display: 'grid',
          placeItems: 'center',
          background: failed ? '#1e293b' : 'transparent',
          color: '#94a3b8',
          fontSize: '2rem',
          ...imageProps.style,
        }}
      >
        {failed ? '📷' : null}
      </span>
    );
  }

  return (
    <img
      src={objectUrl}
      alt={alt}
      onError={(event) => {
        setObjectUrl(null);
        setFailed(true);
        onLoadErrorRef.current?.(event);
      }}
      {...imageProps}
    />
  );
};

export default AuthenticatedImage;
